const PAGE =
  document.querySelector('[data-board-root]')?.dataset?.boardRoot ||
  'launcher';

const ERROR_MESSAGES = [
  'Mr. Hewitt is calibrating the lab...',
  'Even engineering minds pause to think.',
  'The gears are turning... stand by.',
  'This is where ideas begin to form.',
  'Mr. Hewitt is mid-thought...',
  'Great things take time.',
  'System warming up...',
  'Patience. Innovation is loading.'
];

const WEATHER_CODE_MAP = {
  0: 'Clear',
  1: 'Mostly Clear',
  2: 'Partly Cloudy',
  3: 'Cloudy',
  45: 'Fog',
  48: 'Fog',
  51: 'Light Drizzle',
  53: 'Drizzle',
  55: 'Heavy Drizzle',
  61: 'Light Rain',
  63: 'Rain',
  65: 'Heavy Rain',
  71: 'Light Snow',
  73: 'Snow',
  75: 'Heavy Snow',
  77: 'Snow Grains',
  80: 'Rain Showers',
  81: 'Rain Showers',
  82: 'Heavy Showers',
  85: 'Snow Showers',
  86: 'Heavy Snow Showers',
  95: 'Thunderstorm',
  96: 'Storm and Hail',
  99: 'Severe Storm'
};


/* =========================================================
   SCREEN WAKE LOCK
   Agenda and Timer pages only.
   ========================================================= */

let boardWakeLock = null;

async function keepScreenAwake() {
  if (
    PAGE !== 'agenda' &&
    PAGE !== 'timer'
  ) {
    return;
  }

  if (
    !('wakeLock' in navigator) ||
    document.visibilityState !== 'visible'
  ) {
    return;
  }

  if (boardWakeLock) {
    return;
  }

  try {
    boardWakeLock =
      await navigator.wakeLock.request('screen');

    boardWakeLock.addEventListener(
      'release',
      () => {
        boardWakeLock = null;

        /*
         * If the browser releases the lock while
         * the page is still visible, try again.
         */
        if (
          document.visibilityState === 'visible'
        ) {
          window.setTimeout(
            keepScreenAwake,
            250
          );
        }
      },
      { once: true }
    );

    console.log(
      'Board screen wake lock active.'
    );
  } catch (error) {
    console.warn(
      'Screen Wake Lock could not be activated:',
      error
    );

    boardWakeLock = null;
  }
}

async function restoreScreenWakeLock() {
  if (
    document.visibilityState === 'visible' &&
    (
      PAGE === 'agenda' ||
      PAGE === 'timer'
    )
  ) {
    await keepScreenAwake();
  }
}

document.addEventListener(
  'visibilitychange',
  restoreScreenWakeLock
);

window.addEventListener(
  'focus',
  restoreScreenWakeLock
);


function randomMessage() {
  return ERROR_MESSAGES[
    Math.floor(Math.random() * ERROR_MESSAGES.length)
  ];
}

function setText(id, value) {
  const element = document.getElementById(id);

  if (element) {
    element.textContent = value;
  }
}

function getABDay(date = new Date()) {
  return date.getDate() % 2 === 0 ? 'A' : 'B';
}

function getLocalDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getLocalDate(dateKey) {
  const [year, month, day] =
    dateKey.split('-').map(Number);

  return new Date(year, month - 1, day, 12);
}

function isSchoolDay(date, calendar) {
  const dateKey = getLocalDateKey(date);

  if (
    dateKey < calendar.firstStudentDay ||
    dateKey > calendar.lastStudentDay
  ) {
    return false;
  }

  if (
    date.getDay() === 0 ||
    date.getDay() === 6
  ) {
    return false;
  }

  return !calendar.noSchoolDates.includes(dateKey);
}

function getABDayIndex(date = new Date()) {
  const calendar =
    SHARED_SETTINGS?.schedule?.schoolCalendar;

  if (
    !calendar ||
    !isSchoolDay(date, calendar)
  ) {
    return null;
  }

  const targetABDay = getABDay(date);
  const targetDateKey = getLocalDateKey(date);

  let dayIndex = 0;

  for (
    const currentDate =
      getLocalDate(calendar.firstStudentDay);

    getLocalDateKey(currentDate) <= targetDateKey;

    currentDate.setDate(
      currentDate.getDate() + 1
    )
  ) {
    if (
      isSchoolDay(currentDate, calendar) &&
      getABDay(currentDate) === targetABDay
    ) {
      dayIndex += 1;
    }
  }

  return dayIndex - 1;
}

function getABPairIndex(date = new Date()) {
  const calendar =
    SHARED_SETTINGS?.schedule?.schoolCalendar;

  if (
    !calendar ||
    !isSchoolDay(date, calendar)
  ) {
    return null;
  }

  const targetDateKey =
    getLocalDateKey(date);

  let aDayCount = 0;

  for (
    const currentDate =
      getLocalDate(calendar.firstStudentDay);

    getLocalDateKey(currentDate) <= targetDateKey;

    currentDate.setDate(
      currentDate.getDate() + 1
    )
  ) {
    if (
      isSchoolDay(currentDate, calendar) &&
      getABDay(currentDate) === 'A'
    ) {
      aDayCount += 1;
    }
  }

  return aDayCount - 1;
}

function getDailyBoardValue(
  section,
  date = new Date()
) {
  if (section !== 'quote') {
    return null;
  }

  const calendar =
    SHARED_SETTINGS?.schedule?.schoolCalendar;

  if (
    !calendar ||
    !isSchoolDay(date, calendar)
  ) {
    return null;
  }

  const abDay = getABDay(date);
  const dayIndex = getABDayIndex(date);
  const entries = DAILY_QUOTES?.[abDay];

  if (
    dayIndex === null ||
    !Array.isArray(entries) ||
    !entries.length
  ) {
    return null;
  }

  const value =
    entries[dayIndex % entries.length];

  if (
    typeof value === 'string' &&
    value.trim()
  ) {
    return value;
  }

  return null;
}

function getCurrentGrade(now = new Date()) {
  const calendar =
    SHARED_SETTINGS?.schedule?.schoolCalendar;

  const currentMinutes =
    now.getHours() * 60 +
    now.getMinutes();

  const activeGrades =
    SHARED_SETTINGS?.schedule?.activeGrades || [];

  const classSchedule =
    SHARED_SETTINGS?.schedule?.classSchedule || {};

  if (
    calendar &&
    !isSchoolDay(now, calendar)
  ) {
    return null;
  }

  for (const grade of activeGrades) {
    const window =
      classSchedule[grade];

    if (!window) {
      continue;
    }

    const [
      startHour,
      startMinute,
      endHour,
      endMinute
    ] = window;

    const start =
      startHour * 60 +
      startMinute;

    const end =
      endHour * 60 +
      endMinute;

    if (
      currentMinutes >= start &&
      currentMinutes < end
    ) {
      return grade;
    }
  }

  return null;
}

function getActiveClassPeriod(now = new Date()) {
  const calendar =
    SHARED_SETTINGS?.schedule?.schoolCalendar;

  const currentMinutes =
    now.getHours() * 60 +
    now.getMinutes();

  const activeGrades =
    SHARED_SETTINGS?.schedule?.activeGrades || [];

  const classSchedule =
    SHARED_SETTINGS?.schedule?.classSchedule || {};

  if (
    calendar &&
    !isSchoolDay(now, calendar)
  ) {
    return null;
  }

  for (const grade of activeGrades) {
    const window =
      classSchedule[grade];

    if (!window) {
      continue;
    }

    const [
      startHour,
      startMinute,
      endHour,
      endMinute
    ] = window;

    const start =
      startHour * 60 +
      startMinute;

    const end =
      endHour * 60 +
      endMinute;

    if (
      currentMinutes >= start &&
      currentMinutes < end
    ) {
      return {
        grade,
        start,
        end
      };
    }
  }

  return null;
}

function getNextClassPeriod(now = new Date()) {
  const calendar =
    SHARED_SETTINGS?.schedule?.schoolCalendar;

  const activeGrades =
    SHARED_SETTINGS?.schedule?.activeGrades || [];

  const classSchedule =
    SHARED_SETTINGS?.schedule?.classSchedule || {};

  if (
    calendar &&
    !isSchoolDay(now, calendar)
  ) {
    return null;
  }

  const currentMinutes =
    now.getHours() * 60 +
    now.getMinutes() +
    now.getSeconds() / 60;

  let nextPeriod = null;

  for (const grade of activeGrades) {
    const window =
      classSchedule[grade];

    if (!window) {
      continue;
    }

    const [
      startHour,
      startMinute,
      endHour,
      endMinute
    ] = window;

    const start =
      startHour * 60 +
      startMinute;

    const end =
      endHour * 60 +
      endMinute;

    if (start > currentMinutes) {
      if (
        !nextPeriod ||
        start < nextPeriod.start
      ) {
        nextPeriod = {
          grade,
          start,
          end
        };
      }
    }
  }

  return nextPeriod;
}

function formatClassTime(minutes) {
  const hour =
    Math.floor(minutes / 60);

  const minute =
    minutes % 60;

  const date = new Date();

  date.setHours(
    hour,
    minute,
    0,
    0
  );

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
}

function getClassKey(now = new Date()) {
  const grade =
    getCurrentGrade(now);

  if (!grade) {
    return null;
  }

  return `${grade}${getABDay(now)}`;
}

function pickValue(value) {
  if (Array.isArray(value)) {
    if (!value.length) {
      return randomMessage();
    }

    return value[
      Math.floor(
        Math.random() * value.length
      )
    ];
  }

  if (
    typeof value === 'string' &&
    value.trim()
  ) {
    return value;
  }

  return randomMessage();
}

function applyABTheme() {
  document.body.setAttribute(
    'data-day',
    getABDay()
  );
}

function initAgendaBoard() {

  /*
   * AGENDA BOARD BELLRINGER TIMER
   *
   * This is intentionally separate from the
   * Timer Board class countdown.
   *
   * It remains a 3-minute timer that starts
   * at the beginning of each class.
   */

  const bellringerTimerDisplay =
    document.getElementById(
      'bellringer-timer-display'
    );

  const bellringerTimer =
    document.getElementById(
      'bellringer-timer'
    );

  const bellringerTile =
    document.getElementById(
      'bellringer'
    );

  const bellringerTimerDefaultSeconds =
    AGENDA_BOARD?.bellringerTimerSeconds ||
    180;

  let bellringerTimerRemainingSeconds =
    bellringerTimerDefaultSeconds;

  let bellringerTimerId =
    null;

  let bellringerTimerEndTime =
    null;

  let bellringerTimerVisible =
    false;

  let lastAutoBellringerPeriodId =
    null;

  function formatBellringerTime(
    totalSeconds
  ) {
    const minutes =
      Math.floor(totalSeconds / 60);

    const seconds =
      totalSeconds % 60;

    return `${String(minutes)}:${String(
      seconds
    ).padStart(2, '0')}`;
  }

  function updateBellringerTimer() {
    if (bellringerTimerDisplay) {
      bellringerTimerDisplay.textContent =
        formatBellringerTime(
          bellringerTimerRemainingSeconds
        );
    }

    const isRunning =
      Boolean(bellringerTimerId);

    const isEnding =
      bellringerTimerRemainingSeconds > 0 &&
      bellringerTimerRemainingSeconds <= 30;

    const isFinished =
      bellringerTimerRemainingSeconds === 0;

    bellringerTile?.classList.toggle(
      'bellringer-timer-running',
      isRunning
    );

    bellringerTile?.classList.toggle(
      'bellringer-timer-ending',
      isEnding
    );

    bellringerTile?.classList.toggle(
      'bellringer-timer-finished',
      isFinished
    );

    if (bellringerTimer) {
      /*
       * Keep the timer visible at 0:00 during the
       * active class. This also handles a page load
       * after the bellringer has finished.
       */
      bellringerTimer.hidden =
        !bellringerTimerVisible;
    }
  }

  function stopBellringerTimer() {
    if (bellringerTimerId) {
      clearInterval(
        bellringerTimerId
      );

      bellringerTimerId =
        null;
    }
  }

  function tickBellringerTimer() {
    if (!bellringerTimerEndTime) {
      return;
    }

    bellringerTimerRemainingSeconds =
      Math.max(
        0,
        Math.ceil(
          (
            bellringerTimerEndTime -
            Date.now()
          ) / 1000
        )
      );

    updateBellringerTimer();

    if (bellringerTimerRemainingSeconds === 0) {
      stopBellringerTimer();

      bellringerTimerEndTime =
        null;
    }
  }

  function syncBellringerTimerToClassSchedule() {
    const now =
      new Date();

    const activePeriod =
      getActiveClassPeriod(now);

    /*
     * No active class.
     */
    if (!activePeriod) {

      /*
       * If we just left a class, immediately
       * redraw the Agenda Board so it changes
       * from the previous class to the next
       * class or NO CLASS.
       */
      if (
        lastAutoBellringerPeriodId !== null
      ) {
        lastAutoBellringerPeriodId =
          null;

        updateAgendaBoard();
      }

      const nextPeriod =
        getNextClassPeriod(now);

      bellringerTimerVisible =
        Boolean(nextPeriod);

      if (
        bellringerTimerVisible &&
        bellringerTimerRemainingSeconds === 0
      ) {
        bellringerTimerRemainingSeconds =
          bellringerTimerDefaultSeconds;
      }

      updateBellringerTimer();

      return;
    }

    const periodId =
      `${getLocalDateKey(now)}-${activePeriod.grade}-${activePeriod.start}-${activePeriod.end}`;

    /*
     * Same class. Nothing needs to be redrawn.
     */
    if (
      periodId ===
      lastAutoBellringerPeriodId
    ) {
      return;
    }

    /*
     * NEW CLASS HAS STARTED.
     *
     * This is the critical fix.
     *
     * The Agenda data is updated immediately,
     * then the bellringer timer is synchronized
     * to the scheduled class start.
     *
     * No page refresh.
     * No 15-second wait.
     * No 60-second wait.
     */
    lastAutoBellringerPeriodId =
      periodId;

    bellringerTimerVisible =
      true;

    updateAgendaBoard();

    const periodStart =
      new Date(now);

    periodStart.setHours(
      Math.floor(activePeriod.start / 60),
      activePeriod.start % 60,
      0,
      0
    );

    bellringerTimerRemainingSeconds =
      Math.max(
        0,
        Math.ceil(
          (
            periodStart.getTime() +
            bellringerTimerDefaultSeconds * 1000 -
            now.getTime()
          ) / 1000
        )
      );

    stopBellringerTimer();

    if (
      bellringerTimerRemainingSeconds > 0
    ) {
      bellringerTimerEndTime =
        periodStart.getTime() +
        bellringerTimerDefaultSeconds * 1000;

      bellringerTimerId =
        window.setInterval(
          tickBellringerTimer,
          250
        );
    } else {
      bellringerTimerEndTime =
        null;
    }

    updateBellringerTimer();
  }

  function getClassData() {
    const classKey =
      getClassKey();

    if (!classKey) {
      return null;
    }

    return (
      AGENDA_BOARD.classes?.[classKey] ||
      null
    );
  }

  function getGradeContentValue(
    section,
    classKey,
    date = new Date()
  ) {
    if (!classKey) {
      return null;
    }

    const override =
      CLASS_CONTENT_OVERRIDES?.[classKey] ||
      {};

    if (
      typeof override[section] === 'string' &&
      override[section].trim()
    ) {
      return override[section];
    }

    const grade =
      classKey.charAt(0);

    const gradeContent =
      GRADE_BOARD_CONTENT?.[grade];

    if (!gradeContent) {
      return null;
    }

    if (section === 'ideas') {
      return (
        gradeContent.essentialIdea ||
        null
      );
    }

    const pairIndex =
      getABPairIndex(date);

    const rotation =
      gradeContent.rotation;

    if (
      pairIndex === null ||
      !Array.isArray(rotation) ||
      !rotation.length
    ) {
      return null;
    }

    const rotationStartIndex =
      typeof gradeContent.rotationStartDate === 'string'
        ? getABPairIndex(
            getLocalDate(
              gradeContent.rotationStartDate
            )
          )
        : 0;

    const projectPairIndex =
      pairIndex -
      (rotationStartIndex ?? 0);

    const offset =
      Number.isInteger(
        override.rotationOffset
      )
        ? override.rotationOffset
        : 0;

    const rotationIndex =
      (
        (
          projectPairIndex +
          offset
        ) %
        rotation.length +
        rotation.length
      ) %
      rotation.length;

    return (
      rotation[rotationIndex]?.[section] ||
      null
    );
  }

  function updateNoClassDisplay(
    now = new Date()
  ) {
    const nextPeriod =
      getNextClassPeriod(now);

    const nextClassText =
      nextPeriod
        ? `NO CLASS • Next: ${nextPeriod.grade}th Grade at ${formatClassTime(nextPeriod.start)}`
        : 'NO CLASS';

    setText(
      'current-code',
      '----'
    );

    setText(
      'current-ab',
      `${getABDay()} DAY`
    );

    [
      'objective',
      'ideas',
      'bellringer',
      'riddle'
    ].forEach((section) => {
      setText(
        `${section}-content`,
        ''
      );
    });

    const quote =
      getDailyBoardValue(
        'quote',
        now
      );

    setText(
      'quote-content',
      quote || ''
    );

    setText(
      'gradeTitle',
      nextClassText
    );
  }

  function updateHeader() {
    const now =
      new Date();

    const classKey =
      getClassKey(now);

    if (!classKey) {
      updateNoClassDisplay(now);
    } else {
      setText(
        'gradeTitle',
        `${classKey} - ${SHARED_SETTINGS?.schoolName || 'The STEM Lab'}`
      );
    }

   setText(

  'date',
  now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  })

);
  }

  function updateCode() {
    const classKey =
      getClassKey();

    setText(
      'current-ab',
      `${getABDay()} DAY`
    );

    if (!classKey) {
      setText(
        'current-code',
        '----'
      );

      return;
    }

    const classData =
      getClassData();

    setText(
      'current-code',
      classData?.code || '----'
    );
  }

  function updateSection(section) {
    const now =
      new Date();

    const classKey =
      getClassKey(now);

    const elementId =
      `${section}-content`;

    const dailyValue =
      getDailyBoardValue(
        section,
        now
      );

    if (dailyValue) {
      setText(
        elementId,
        dailyValue
      );

      return;
    }

    if (!classKey) {
      if (section !== 'quote') {
        setText(
          elementId,
          ''
        );
      }

      return;
    }

    if (section === 'riddle') {
      const riddles =
        CLASS_RIDDLE_BANKS?.[classKey];

      const riddleIndex =
        CLASS_CONTENT_OVERRIDES?.[classKey]
          ?.riddleIndex;

      const riddle =
        Array.isArray(riddles) &&
        riddles.length
          ? riddles[
              (
                (
                  Number.isInteger(riddleIndex)
                    ? riddleIndex
                    : 0
                ) %
                riddles.length +
                riddles.length
              ) %
              riddles.length
            ]
          : null;

      if (
        typeof riddle === 'string' &&
        riddle.trim()
      ) {
        setText(
          elementId,
          riddle
        );

        return;
      }
    }

    const gradeValue =
      getGradeContentValue(
        section,
        classKey,
        now
      );

    if (gradeValue) {
      setText(
        elementId,
        gradeValue
      );

      return;
    }

    const classData =
      getClassData();

    if (
      !classData ||
      !(section in classData)
    ) {
      setText(
        elementId,
        randomMessage()
      );

      return;
    }

    setText(
      elementId,
      pickValue(
        classData[section]
      )
    );
  }

  async function fetchWeather() {
    if (
      !SHARED_SETTINGS?.weather?.enabled
    ) {
      setText(
        'temp',
        '--°F'
      );

      setText(
        'condition',
        'Offline mode'
      );

      return;
    }

    try {
      const {
        latitude,
        longitude
      } = SHARED_SETTINGS.weather;

      const url =
        'https://api.open-meteo.com/v1/forecast' +
        `?latitude=${latitude}&longitude=${longitude}` +
        '&current=temperature_2m,weather_code&temperature_unit=fahrenheit';

      const response =
        await fetch(url);

      if (!response.ok) {
        throw new Error();
      }

      const data =
        await response.json();

      const current =
        data?.current;

      if (
        !current ||
        typeof current.temperature_2m !==
        'number'
      ) {
        throw new Error();
      }

      setText(
        'temp',
        `${Math.round(current.temperature_2m)}°F`
      );

      setText(
        'condition',
        WEATHER_CODE_MAP[
          current.weather_code
        ] ||
        'Current Weather'
      );
    } catch {
      setText(
        'temp',
        '--°F'
      );

      setText(
        'condition',
        'Weather Offline'
      );
    }
  }

  function updateAgendaBoard() {
    applyABTheme();
    updateHeader();
    updateCode();

    [
      'objective',
      'ideas',
      'bellringer',
      'quote',
      'riddle'
    ].forEach(updateSection);
  }

  /*
   * Initial Agenda load.
   */
  applyABTheme();
  updateAgendaBoard();
  updateBellringerTimer();
  syncBellringerTimerToClassSchedule();

  /*
   * Wake Lock immediately on page load.
   */
  keepScreenAwake();

  /*
   * Weather immediately.
   */
  fetchWeather();

  /*
   * Date and time remain live every second.
   */
  setInterval(
    updateHeader,
    1000
  );

  /*
   * Existing 60-second backup refresh.
   *
   * This is NOT responsible for class transitions.
   * The 250 ms schedule watcher handles those.
   */
  setInterval(
    updateAgendaBoard,
    60 * 1000
  );

  /*
   * CRITICAL FIX:
   *
   * Check the class schedule four times per second.
   *
   * This means the Agenda data changes automatically
   * at the class transition instead of waiting 15 seconds.
   */
  setInterval(
    syncBellringerTimerToClassSchedule,
    250
  );

  /*
   * Wake Lock backup check.
   */
  setInterval(
    keepScreenAwake,
    30 * 1000
  );

  if (
    SHARED_SETTINGS?.weather?.enabled
  ) {
    setInterval(
      fetchWeather,
      10 * 60 * 1000
    );
  }
}

function initTimerBoard() {

  /*
   * Keep the Chromebook awake on the Timer Board.
   */
  keepScreenAwake();

  const timerDisplay =
    document.getElementById(
      'timer-display'
    );

  const timerStatus =
    document.getElementById(
      'timer-status'
    );

  const timerCard =
    document.querySelector(
      '.timer-card'
    );

  /*
   * Horizontal timer progress bar.
   *
   * The HTML must contain:
   *
   * <div class="timer-bar-progress"
   *      id="timer-bar-progress"></div>
   *
   * The CSS controls the appearance.
   * JavaScript controls the width through
   * scaleX().
   */
  const timerBarProgress =
    document.getElementById(
      'timer-bar-progress'
    );

  const activeClassBadge =
    document.getElementById(
      'active-class'
    );

  const startButton =
    document.getElementById(
      'start-btn'
    );

  const pauseButton =
    document.getElementById(
      'pause-btn'
    );

  const resetButton =
    document.getElementById(
      'reset-btn'
    );

  const presetButtons =
    document.querySelectorAll(
      '.preset'
    );

  const cleanupButton =
    document.getElementById(
      'cleanup-btn'
    );

  /*
   * AUTOMATIC CLASS TIMER
   *
   * The school day class period is 63 minutes.
   * The final 10 minutes are cleanup.
   *
   * Therefore:
   *
   * 63 - 10 = 53 minutes of work time.
   *
   * The automatic timer starts at the actual
   * beginning of class and reaches 0:00 when
   * cleanup begins.
   *
   * This is completely separate from the
   * 3-minute Agenda Board bellringer timer.
   */

  const CLASS_CLEANUP_MINUTES = 10;
  const CLASS_WORK_MINUTES = 53;

  const CLASS_WORK_SECONDS =
    CLASS_WORK_MINUTES * 60;

  let defaultSeconds =
    CLASS_WORK_SECONDS;

  let remainingSeconds =
    CLASS_WORK_SECONDS;

  let countdownId =
    null;

  let endTime =
    null;

  let lastAutoPeriodId =
    null;

  let audioContext =
    null;

  let hasPlayedEndSound =
    false;

  function getAudioContext() {
    const AudioContextClass =
      window.AudioContext ||
      window.webkitAudioContext;

    if (!AudioContextClass) {
      return null;
    }

    if (!audioContext) {
      audioContext =
        new AudioContextClass();
    }

    return audioContext;
  }

  function unlockAudio() {
    const context =
      getAudioContext();

    if (
      context &&
      context.state === 'suspended'
    ) {
      context
        .resume()
        .catch(() => { });
    }
  }

  function playEndSound() {
    const context =
      getAudioContext();

    if (!context) {
      return;
    }

    const now =
      context.currentTime;

    const notes = [
      880,
      1174.66,
      1567.98
    ];

    notes.forEach(
      (
        frequency,
        index
      ) => {
        const oscillator =
          context.createOscillator();

        const gain =
          context.createGain();

        const start =
          now + index * 0.16;

        const end =
          start + 0.22;

        oscillator.type =
          'sine';

        oscillator.frequency.setValueAtTime(
          frequency,
          start
        );

        gain.gain.setValueAtTime(
          0.0001,
          start
        );

        gain.gain.exponentialRampToValueAtTime(
          0.18,
          start + 0.02
        );

        gain.gain.exponentialRampToValueAtTime(
          0.0001,
          end
        );

        oscillator.connect(gain);
        gain.connect(
          context.destination
        );

        oscillator.start(start);
        oscillator.stop(end);
      }
    );
  }

  function updateScheduleDisplay() {
    const grade =
      getCurrentGrade();

    if (!grade) {
      if (activeClassBadge) {
        activeClassBadge.textContent =
          'NO ACTIVE CLASS';
      }

      timerCard?.classList.add(
        'no-active-class'
      );

      document
        .querySelectorAll(
          '.project-card'
        )
        .forEach((card) => {
          card.classList.remove(
            'active'
          );
        });

      return;
    }

    timerCard?.classList.remove(
      'no-active-class'
    );

    const classKey =
      `${grade}${getABDay()}`;

    if (activeClassBadge) {
      activeClassBadge.textContent =
        `${classKey} - ${SHARED_SETTINGS?.schoolName || 'The STEM Lab'}`;
    }

    document
      .querySelectorAll(
        '.project-card'
      )
      .forEach((card) => {
        card.classList.toggle(
          'active',
          card.dataset.grade ===
          grade
        );
      });
  }

  function formatTime(
    totalSeconds
  ) {
    const minutes =
      Math.floor(
        totalSeconds / 60
      );

    const seconds =
      totalSeconds % 60;

    return `${String(minutes)}:${String(
      seconds
    ).padStart(2, '0')}`;
  }

  function setTimerState() {
    const warningSeconds =
      (
        TIMER_BOARD?.timer
          ?.warningMinutes ||
        10
      ) * 60;

    const endingSeconds =
      (
        TIMER_BOARD?.timer
          ?.endingMinutes ||
        3
      ) * 60;

    const isWarning =
      remainingSeconds <=
      warningSeconds &&
      remainingSeconds >
      endingSeconds;

    const isEnding =
      remainingSeconds <=
      endingSeconds &&
      remainingSeconds > 0;

    timerCard?.classList.toggle(
      'warning',
      isWarning
    );

    timerCard?.classList.toggle(
      'ending',
      isEnding
    );

    timerCard?.classList.toggle(
      'timer-active',
      Boolean(countdownId)
    );

    timerCard?.classList.toggle(
      'timer-warning',
      isWarning
    );

    timerCard?.classList.toggle(
      'timer-ending',
      isEnding
    );

    if (
      remainingSeconds === 0
    ) {
      timerStatus.textContent =
        'CLEANUP TIME';
    } else if (countdownId) {
      timerStatus.textContent =
        'Running';
    } else if (
      remainingSeconds !==
      defaultSeconds
    ) {
      timerStatus.textContent =
        'Paused';
    } else {
      timerStatus.textContent =
        'Ready';
    }
  }

  function updateTimerDisplay() {
    if (timerDisplay) {
      timerDisplay.textContent =
        formatTime(
          remainingSeconds
        );
    }

    /*
     * HORIZONTAL PROGRESS BAR
     *
     * 1.00 = full bar
     * 0.50 = half bar
     * 0.00 = empty bar
     */
    if (timerBarProgress) {
      const progress =
        defaultSeconds > 0
          ? remainingSeconds /
            defaultSeconds
          : 0;

      const clampedProgress =
        Math.max(
          0,
          Math.min(
            1,
            progress
          )
        );

      timerBarProgress.style.transform =
        `scaleX(${clampedProgress})`;

      timerBarProgress.setAttribute(
        'aria-valuenow',
        String(
          Math.round(
            clampedProgress * 100
          )
        )
      );
    }

    setTimerState();
  }

  function stopTimer() {
    if (countdownId) {
      clearInterval(
        countdownId
      );

      countdownId =
        null;
    }
  }

  function tickTimer() {
    if (!endTime) {
      return;
    }

    const secondsLeft =
      Math.max(
        0,
        Math.ceil(
          (
            endTime -
            Date.now()
          ) / 1000
        )
      );

    remainingSeconds =
      secondsLeft;

    updateTimerDisplay();

    if (
      secondsLeft === 0
    ) {
      stopTimer();

      endTime =
        null;

      if (
        !hasPlayedEndSound
      ) {
        hasPlayedEndSound =
          true;

        playEndSound();
      }
    }
  }

  function startTimer() {
    if (countdownId) {
      return;
    }

    if (
      remainingSeconds === 0
    ) {
      resetTimer();
    }

    unlockAudio();

    endTime =
      Date.now() +
      remainingSeconds * 1000;

    countdownId =
      window.setInterval(
        tickTimer,
        250
      );

    tickTimer();
  }

  function pauseTimer() {
    if (!countdownId) {
      return;
    }

    tickTimer();

    stopTimer();

    endTime =
      null;

    updateTimerDisplay();
  }

  function resetTimer(
    minutes =
      CLASS_WORK_MINUTES
  ) {
    stopTimer();

    endTime =
      null;

    defaultSeconds =
      minutes * 60;

    remainingSeconds =
      defaultSeconds;

    hasPlayedEndSound =
      false;

    updateTimerDisplay();
  }

  function setClassTimer(
    options = {}
  ) {
    const {
      autoStart = false
    } = options;

    const now =
      new Date();

    const activePeriod =
      getActiveClassPeriod(
        now
      );

    if (!activePeriod) {
      return false;
    }

    const classStartSeconds =
      activePeriod.start * 60;

    const classEndSeconds =
      activePeriod.end * 60;

    const cleanupStartSeconds =
      classEndSeconds -
      CLASS_CLEANUP_MINUTES * 60;

    const nowSeconds =
      now.getHours() * 3600 +
      now.getMinutes() * 60 +
      now.getSeconds();

    const secondsUntilCleanup =
      cleanupStartSeconds -
      nowSeconds;

    const scheduledWorkSeconds =
      cleanupStartSeconds -
      classStartSeconds;

    defaultSeconds =
      scheduledWorkSeconds > 0
        ? scheduledWorkSeconds
        : CLASS_WORK_SECONDS;

    if (
      secondsUntilCleanup <= 0
    ) {
      stopTimer();

      endTime =
        null;

      remainingSeconds =
        0;

      hasPlayedEndSound =
        false;

      updateTimerDisplay();

      return true;
    }

    stopTimer();

    endTime =
      null;

    remainingSeconds =
      Math.min(
        defaultSeconds,
        secondsUntilCleanup
      );

    hasPlayedEndSound =
      false;

    updateTimerDisplay();

    if (autoStart) {
      unlockAudio();

      endTime =
        Date.now() +
        remainingSeconds * 1000;

      countdownId =
        window.setInterval(
          tickTimer,
          250
        );

      tickTimer();
    }

    return true;
  }

  function syncTimerToClassSchedule() {
    const now =
      new Date();

    const activePeriod =
      getActiveClassPeriod(
        now
      );

    const activePeriodId =
      activePeriod
        ? `${getLocalDateKey(now)}-${activePeriod.grade}-${activePeriod.start}-${activePeriod.end}`
        : null;

    if (!activePeriodId) {
      lastAutoPeriodId =
        null;

      return;
    }

    if (
      activePeriodId ===
      lastAutoPeriodId
    ) {
      return;
    }

    const applied =
      setClassTimer({
        autoStart: true
      });

    if (applied) {
      lastAutoPeriodId =
        activePeriodId;
    }
  }

  function renderReminders() {
    const remindersRoot =
      document.getElementById(
        'reminders-content'
      );

    if (!remindersRoot) {
      return;
    }

    const reminderTitle =
      TIMER_BOARD?.reminders
        ?.title ||
      'Reminders';

    const grades =
      TIMER_BOARD?.reminders
        ?.grades ||
      {};

    const activeGrade =
      getCurrentGrade();

    let gradeMarkup =
      '';

    if (activeGrade) {
      const text =
        grades[activeGrade];

      if (text) {
        gradeMarkup = `
          <section class="reminder-grade">
            <h3>${activeGrade}th Grade</h3>
            <p>${text}</p>
          </section>
        `;
      }
    } else {
      gradeMarkup = `
        <section class="reminder-grade">
          <p>No active class.</p>
        </section>
      `;
    }

    remindersRoot.innerHTML =
      `<h2>${reminderTitle}</h2>${gradeMarkup}`;
  }

  function renderProjects() {
    const projects =
      TIMER_BOARD?.projects ||
      {};

    for (
      const grade of [
        '6',
        '7',
        '8'
      ]
    ) {
      const project =
        projects[grade];

      if (!project) {
        continue;
      }

      const icon =
        document.getElementById(
          `icon-${grade}`
        );

      const title =
        document.getElementById(
          `title-${grade}`
        );

      const current =
        document.getElementById(
          `current-${grade}`
        );

      const next =
        document.getElementById(
          `next-${grade}`
        );

      if (icon) {
        icon.textContent =
          project.icon ||
          '🧪';
      }

      if (title) {
        title.textContent =
          project.title ||
          `${grade}th Grade Project`;
      }

      if (current) {
        current.textContent =
          project.current ||
          '';
      }

      if (next) {
        next.textContent =
          project.next ||
          '';
      }
    }
  }

  function attachEvents() {
    startButton?.addEventListener(
      'click',
      startTimer
    );

    pauseButton?.addEventListener(
      'click',
      pauseTimer
    );

    resetButton?.addEventListener(
      'click',
      () => {
        unlockAudio();
        resetTimer();
      }
    );

    presetButtons.forEach(
      (button) => {
        button.addEventListener(
          'click',
          () => {
            unlockAudio();

            const minutes =
              Number(
                button.dataset.minutes
              );

            if (
              !Number.isFinite(
                minutes
              ) ||
              minutes <= 0
            ) {
              return;
            }

            resetTimer(
              minutes
            );
          }
        );
      }
    );

    cleanupButton?.addEventListener(
      'click',
      () => {
        unlockAudio();

        setClassTimer();
      }
    );
  }

  renderReminders();
  renderProjects();
  attachEvents();
  updateScheduleDisplay();

  resetTimer(
    CLASS_WORK_MINUTES
  );

  syncTimerToClassSchedule();

  /*
   * Keep the normal display updates.
   */
  setInterval(
    updateScheduleDisplay,
    60 * 1000
  );

  setInterval(
    renderReminders,
    60 * 1000
  );

  /*
   * CRITICAL FIX:
   *
   * The Timer Board now checks the schedule
   * every 250 ms instead of every 15 seconds.
   *
   * The timer still only initializes once
   * when a new class period begins, so this
   * does not repeatedly restart the timer.
   */
  setInterval(
    syncTimerToClassSchedule,
    250
  );

  /*
   * Keep the Chromebook awake.
   */
  setInterval(
    keepScreenAwake,
    30 * 1000
  );
}

function initResearchBoard() {

  function setList(
    id,
    items
  ) {
    const element =
      document.getElementById(id);

    if (!element) {
      return;
    }

    element.innerHTML =
      '';

    items.forEach(
      (item) => {
        const li =
          document.createElement(
            'li'
          );

        li.textContent =
          item;

        element.appendChild(
          li
        );
      }
    );
  }

  function updateResearchHeader() {
    const now =
      new Date();

    const grade =
      getCurrentGrade(now);

    const classText =
      grade
        ? `${grade}${getABDay(now)}`
        : 'NO CLASS';

    setText(
      'research-date',
      now.toLocaleDateString(
        undefined,
        {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        }
      ) +
      ' | ' +
      `${classText} - ${SHARED_SETTINGS?.schoolName || 'The STEM Lab'}`
    );
  }

  applyABTheme();
  updateResearchHeader();

  setText(
    'research-focus-content',
    RESEARCH_BOARD?.focus ||
    ''
  );

  setText(
    'research-question-content',
    RESEARCH_BOARD?.question ||
    ''
  );

  setText(
    'research-checkpoint-content',
    RESEARCH_BOARD?.checkpoint ||
    ''
  );

  setList(
    'research-sources-content',
    RESEARCH_BOARD?.sourceRules ||
    []
  );

  setList(
    'research-find-content',
    RESEARCH_BOARD?.whatToFind ||
    []
  );

  setList(
    'research-stems-content',
    RESEARCH_BOARD?.helpfulStems ||
    []
  );

  setInterval(
    updateResearchHeader,
    60 * 1000
  );
}

if (PAGE === 'agenda') {
  initAgendaBoard();
} else if (PAGE === 'timer') {
  initTimerBoard();
} else if (PAGE === 'research') {
  initResearchBoard();
}